import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import { Car, Calendar, User, IdentificationCard, CheckCircle, ArrowRight, ArrowLeft, UploadSimple, Sparkle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${API}/api/public/booking` });

const fmtEUR = v => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

const todayPlus = n => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

/**
 * Öffentliche Buchungs-Seite unter /buchen.
 * 4 Schritte: Zeitraum → Fahrzeug → Kontakt/FS → Bestätigung
 */
export default function PublicBooking() {
  const [step, setStep] = useState(1);
  const [company, setCompany] = useState(null);
  const [dates, setDates] = useState({ start_date: todayPlus(1), end_date: todayPlus(4) });
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [contact, setContact] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    address: '', postal_code: '', city: '', birthdate: '', notes: '',
  });
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseOcr, setLicenseOcr] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { publicApi.get('/company').then(r => setCompany(r.data)).catch(() => {}); }, []);

  const searchVehicles = async () => {
    if (!dates.start_date || !dates.end_date || dates.end_date <= dates.start_date) {
      toast.error('Bitte gültigen Zeitraum wählen'); return;
    }
    setLoading(true);
    try {
      const r = await publicApi.get('/vehicles', { params: dates });
      setVehicles(r.data.vehicles || []);
      if ((r.data.vehicles || []).length === 0) toast.error('Kein Fahrzeug in diesem Zeitraum verfügbar');
      else setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Suche');
    } finally { setLoading(false); }
  };

  const submitBooking = async () => {
    if (!contact.first_name || !contact.last_name || !contact.email) {
      toast.error('Bitte Name und E-Mail ausfüllen'); return;
    }
    setLoading(true);
    try {
      const r = await publicApi.post('/create', {
        vehicle_id: selectedVehicle.id,
        start_date: dates.start_date, end_date: dates.end_date,
        ...contact,
      });
      const { request_id, upload_token, booking_number, total_price } = r.data;
      let ocr = null;
      if (licenseFile) {
        const fd = new FormData(); fd.append('file', licenseFile);
        try {
          const up = await publicApi.post(`/${request_id}/license?token=${upload_token}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          ocr = up.data.ocr;
        } catch (e) {
          console.warn('License upload failed', e);
        }
      }
      setConfirmation({ booking_number, total_price, request_id, ocr });
      setLicenseOcr(ocr);
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Anlegen der Buchung');
    } finally { setLoading(false); }
  };

  const primary = company?.primary_color || '#E60000';
  const days = selectedVehicle?.days || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-testid="public-booking-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {company?.logo_url && (
              <img src={`${API}/api/company-logo`} alt="Logo" className="h-10" onError={e => e.target.style.display = 'none'}/>
            )}
            <div>
              <h1 className="text-lg font-bold text-[#111418]">{company?.company_name || 'AutoVermietung'}</h1>
              <p className="text-xs text-[#525866]">Online-Buchung · Schnell & unkompliziert</p>
            </div>
          </div>
          <div className="text-right text-xs text-[#525866] hidden sm:block">
            {company?.company_phone && <p>☎ {company.company_phone}</p>}
            {company?.company_email && <p>{company.company_email}</p>}
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6 text-xs">
          {['Zeitraum', 'Fahrzeug', 'Kontakt & Führerschein', 'Bestätigung'].map((label, i) => {
            const n = i + 1;
            return (
              <div key={n} className={`flex-1 flex items-center ${i < 3 ? 'after:content-[""] after:h-0.5 after:flex-1 after:mx-2 ' + (step > n ? 'after:bg-emerald-500' : 'after:bg-gray-200') : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${step >= n ? '' : 'bg-gray-300'}`} style={{ background: step >= n ? primary : undefined }}>
                  {step > n ? <CheckCircle weight="fill" size={16}/> : n}
                </div>
                <span className={`ml-2 hidden md:inline ${step >= n ? 'font-semibold text-[#111418]' : 'text-[#8A94A6]'}`}>{label}</span>
              </div>
            );
          })}
        </div>

        {/* STEP 1: Dates */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow-sm p-6" data-testid="step-dates">
            <h2 className="text-xl font-bold text-[#111418] mb-4 flex items-center gap-2"><Calendar weight="bold"/> Zeitraum wählen</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#525866] mb-1">Abholung</label>
                <Input type="date" value={dates.start_date} onChange={e => setDates(d => ({...d, start_date: e.target.value}))} data-testid="input-start-date"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#525866] mb-1">Rückgabe</label>
                <Input type="date" value={dates.end_date} min={dates.start_date} onChange={e => setDates(d => ({...d, end_date: e.target.value}))} data-testid="input-end-date"/>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={searchVehicles} disabled={loading} className="btn-primary" data-testid="btn-search-vehicles">
                {loading ? 'Suche…' : 'Verfügbare Fahrzeuge anzeigen'}<ArrowRight weight="bold" size={16} className="ml-1"/>
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2: Vehicle */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-vehicle">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold text-[#111418] flex items-center gap-2"><Car weight="bold"/> Fahrzeug wählen ({vehicles.length})</h2>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="btn-back-1"><ArrowLeft size={14} className="mr-1"/>Zeitraum ändern</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map(v => (
                <div key={v.id} onClick={() => { setSelectedVehicle(v); setStep(3); }}
                     className={`bg-white rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition border-2 ${selectedVehicle?.id === v.id ? 'border-red-500' : 'border-transparent'}`}
                     data-testid={`vehicle-card-${v.id}`}>
                  {v.primary_image_url ? (
                    <img src={v.primary_image_url.startsWith('http') ? v.primary_image_url : `${API}/api/storage/${v.primary_image_url}`} className="w-full h-40 object-cover bg-gray-100" alt={v.model} onError={e => e.target.style.display='none'}/>
                  ) : (
                    <div className="w-full h-40 bg-gray-100 flex items-center justify-center text-gray-400"><Car size={48} weight="thin"/></div>
                  )}
                  <div className="p-3">
                    <p className="font-bold">{v.brand} {v.model}</p>
                    <p className="text-xs text-[#525866]">{v.category} · {v.transmission || '—'} · {v.fuel_type || '—'}</p>
                    <div className="mt-2 flex items-baseline justify-between">
                      <span className="text-xs text-[#525866]">{v.days} Tage</span>
                      <span className="text-lg font-bold" style={{ color: primary }}>{fmtEUR(v.total_price)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 3: Contact + License */}
        {step === 3 && selectedVehicle && (
          <div className="space-y-4" data-testid="step-contact">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#111418] flex items-center gap-2"><User weight="bold"/> Ihre Daten</h2>
              <Button variant="outline" onClick={() => setStep(2)} data-testid="btn-back-2"><ArrowLeft size={14} className="mr-1"/>Fahrzeug ändern</Button>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-3">
              {/* Summary */}
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center justify-between">
                <div>
                  <p className="font-semibold">{selectedVehicle.brand} {selectedVehicle.model}</p>
                  <p className="text-xs text-[#525866]">{dates.start_date} – {dates.end_date} · {days} Tage</p>
                </div>
                <p className="text-xl font-bold" style={{ color: primary }}>{fmtEUR(selectedVehicle.total_price)}</p>
              </div>

              {/* Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ['first_name', 'Vorname *'], ['last_name', 'Nachname *'],
                  ['email', 'E-Mail *', 'email'], ['phone', 'Telefon', 'tel'],
                  ['address', 'Straße / Hausnr'], ['birthdate', 'Geburtsdatum', 'date'],
                  ['postal_code', 'PLZ'], ['city', 'Ort'],
                ].map(([k, l, t]) => (
                  <div key={k}>
                    <label className="block text-xs font-semibold text-[#525866] mb-1">{l}</label>
                    <Input type={t || 'text'} value={contact[k] || ''} onChange={e => setContact(c => ({...c, [k]: e.target.value}))} data-testid={`input-${k}`}/>
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#525866] mb-1">Notizen (optional)</label>
                <textarea rows={2} value={contact.notes} onChange={e => setContact(c => ({...c, notes: e.target.value}))} className="w-full px-3 py-2 border border-[#E2E4E9] rounded"/>
              </div>

              {/* License Upload */}
              <div className="mt-4 border-t border-[#E2E4E9] pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <IdentificationCard weight="bold" size={18} className="text-blue-600"/>
                  <p className="font-semibold">Führerschein hochladen</p>
                  <span className="text-xs text-[#525866]">(empfohlen — beschleunigt Freigabe)</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" capture="environment" onChange={e => setLicenseFile(e.target.files?.[0])} className="text-sm" data-testid="input-license"/>
                  {licenseFile && <span className="text-xs text-emerald-700">✓ {licenseFile.name}</span>}
                </div>
                <p className="text-xs text-[#8A94A6] mt-1">
                  <Sparkle size={10} className="inline mr-1"/>Wird per KI ausgelesen (Name, Klassen, Ablauf).
                </p>
              </div>

              <div className="mt-4 flex justify-end">
                <Button onClick={submitBooking} disabled={loading} className="btn-primary" data-testid="btn-submit">
                  {loading ? 'Sende Anfrage…' : 'Verbindlich anfragen'}<ArrowRight size={16} weight="bold" className="ml-1"/>
                </Button>
              </div>
              <p className="text-xs text-[#8A94A6] mt-1 text-center">Anfrage wird von unserem Team geprüft — Sie erhalten E-Mail-Bestätigung.</p>
            </div>
          </div>
        )}

        {/* STEP 4: Confirmation */}
        {step === 4 && confirmation && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center" data-testid="step-confirmation">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle weight="fill" size={40} className="text-emerald-600"/>
            </div>
            <h2 className="text-2xl font-bold mb-2">Anfrage eingegangen!</h2>
            <p className="text-[#525866] mb-4">
              Vielen Dank, <b>{contact.first_name}</b>. Wir haben Ihre Anfrage erhalten.
            </p>
            <div className="inline-block bg-[#F7F8FA] rounded p-4 text-left mb-4">
              <p className="text-sm"><b>Buchungsnr.:</b> <span className="font-mono">{confirmation.booking_number}</span></p>
              <p className="text-sm"><b>Zeitraum:</b> {dates.start_date} – {dates.end_date}</p>
              <p className="text-sm"><b>Fahrzeug:</b> {selectedVehicle.brand} {selectedVehicle.model}</p>
              <p className="text-sm"><b>Voraussichtlicher Preis:</b> {fmtEUR(confirmation.total_price)}</p>
            </div>
            {licenseOcr && licenseOcr.first_name && (
              <div className="inline-block bg-blue-50 border border-blue-200 rounded p-3 text-left text-xs mb-4">
                <p className="font-semibold text-blue-800 mb-1"><Sparkle size={12} className="inline mr-1"/>Führerschein erkannt:</p>
                <p>Name: {licenseOcr.first_name} {licenseOcr.last_name}</p>
                {licenseOcr.classes && <p>Klassen: {licenseOcr.classes.join(', ')}</p>}
                {licenseOcr.license_expiry && <p>Ablauf: {licenseOcr.license_expiry}</p>}
              </div>
            )}
            <p className="text-sm text-[#525866] mt-2">
              Sie erhalten in Kürze eine E-Mail mit Ihrer <b>Bestätigung</b> an <b>{contact.email}</b>.
            </p>
            <div className="mt-6">
              <Button onClick={() => { setStep(1); setSelectedVehicle(null); setLicenseFile(null); setConfirmation(null); }} variant="outline" data-testid="btn-new-booking">
                Weitere Buchung starten
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
